/**
 * Showing and saving a file the API returns as bytes (a contract PDF, a traveller's scan).
 *
 * A fetched file is opened from a `blob:` URL, and a `blob:` URL runs in the app's own origin:
 * a script inside it would have the session. So the type a file is opened as is never the one
 * the server or the uploader declared. Only the four picture and document types below open in
 * a tab, and the bytes are re-wrapped with that exact type. Anything else is saved to disk as
 * `application/octet-stream`, which a browser downloads and does not render.
 *
 * The object URL is released after a minute: long enough for the new tab to finish loading it,
 * and no longer, so a page left open does not keep a copy of the file in memory.
 */

const RELEASE_AFTER_MS = 60_000;

/** The only types that open in a tab. Everything else is downloaded. */
export const OPENABLE_TYPES: readonly string[] = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
];

/** What to do with a fetched file: open it in a tab, or save it. The blob is already re-wrapped. */
export type FileAction =
  | { readonly mode: "open"; readonly blob: Blob }
  | { readonly mode: "download"; readonly blob: Blob };

/**
 * Decides how a fetched file is presented. Pure, so the rule is tested without a browser.
 * The declared type is read from the blob, its parameters (`; charset=…`) are dropped, and the
 * result is compared exactly against the allowlist. An empty or unknown type downloads.
 */
export function planFileOpen(blob: Blob): FileAction {
  const declared = blob.type.split(";")[0]?.trim().toLowerCase() ?? "";
  if (OPENABLE_TYPES.includes(declared)) {
    return { mode: "open", blob: new Blob([blob], { type: declared }) };
  }
  return { mode: "download", blob: new Blob([blob], { type: "application/octet-stream" }) };
}

/** Raised when the browser refused the new tab, so the person is told rather than left with nothing. */
export class PopupBlockedError extends Error {
  constructor() {
    super("The browser blocked the new tab.");
    this.name = "PopupBlockedError";
  }
}

/**
 * Opens a fetched file in a new tab when its type is allowlisted, and saves it under
 * `fileName` otherwise. The tab is opened first, synchronously, because a browser blocks a
 * tab opened after the awaited fetch; if the file turns out not to open, that tab is closed
 * and the file is saved instead.
 */
export async function openFetchedFile(load: () => Promise<Blob>, fileName: string): Promise<void> {
  const opened = window.open("", "_blank");
  if (opened === null) {
    throw new PopupBlockedError();
  }
  opened.opener = null;

  let action: FileAction;
  try {
    action = planFileOpen(await load());
  } catch (error) {
    opened.close();
    throw error;
  }

  if (action.mode === "download") {
    opened.close();
    saveFile(action.blob, fileName);
    return;
  }
  const url = URL.createObjectURL(action.blob);
  opened.location.href = url;
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, RELEASE_AFTER_MS);
}

/**
 * Saves bytes under a file name by clicking a temporary link. The bytes are always saved as
 * `application/octet-stream`, whatever type the caller holds, so a saved file is never
 * rendered from the app's origin.
 */
export function saveFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(new Blob([blob], { type: "application/octet-stream" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, RELEASE_AFTER_MS);
}
